import React, { useState, useEffect, useMemo } from 'react';
import { Footer, Header } from './headerFooter';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { EDUC_OPTIONS } from './constants';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { getHost } from './config';
import FilterSidebar from './FilterSidebar';
import { initialSidebarOpen, collapseFilterSidebarOnMobile, joinFilterSummaryParts } from './filterSidebarUtils';
import './custom.scss';

const TABLE_COLUMNS = [
    { key: 'location', label: 'Δήμος' },
    { key: 'deUnit', label: 'Δημοτική Ενότητα' },
    { key: 'community', label: 'Κοινότητα' },
    { key: 'name', label: 'Ονομασία' },
    { key: 'category', label: 'Κατηγορία Μοριοδότησης' },
];

function NonReachablePlaces() {

    const navigate = useNavigate();
    const [educType, setEducType] = useState('');
    const [place, setPlace] = useState('');
    const [placeOptions, setPlaceOptions] = useState([]);
    const [placeOptionsLoading, setPlaceOptionsLoading] = useState(false); // Loading state for place options fetch
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false); // New loading state
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc'); // ascending order by default
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    const handleLogout = () => {
        // Clear the authToken from localStorage
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());

        // Redirect to the login page
        navigate('/', { replace: true });
    }

    useEffect(() => {
            if (educType) {
                setPlaceOptionsLoading(true);
                const url = `${getHost()}/api/non-reachable-places?educ_type=${educType}`;
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                })
                    .then(response => response.json())
                    .then(data => setPlaceOptions(data.results))
                    .catch(error => console.error('Error fetching specialties:', error))
                    .finally(() => setPlaceOptionsLoading(false));
            } else {
                setPlaceOptions([]); // Clear specialties when yearRange is not selected
            }
        }, [educType]);

        const handleSubmit = () => {
            setLoading(true);
            const url = `${getHost()}/api/non-reachable`;
            const requestBody = {
                educ_type: educType,
                place: place
            };

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                cache: 'no-store',
                body: JSON.stringify(requestBody),
            })
                .then(response => response.json())
                .then(data => {
                    const results = data.results;

                    const tableData = [];
                    const locations = new Set();

                    results.forEach(row => {
                        // adjust keys if you keep Greek column names in backend
                        const location = row["Δήμος"]; 
                        const deUnit = row["Δημοτική Ενότητα"];
                        const community = row["Κοινότητα"];
                        const name = row["Ονομασία"];
                        const category = row["Κατηγορία Μοριοδότησης"];

                        locations.add(location);

                        tableData.push({
                            location,
                            deUnit,
                            community,
                            name,
                            category
                        });
                    });
                    
                    setData(tableData);
                })
                .catch(error => console.error('Error fetching data:', error))
                .finally(() => {
                    setLoading(false); // Hide loading indicator regardless of success/failure
                });

        };

        const handleSort = (column) => {
            const order = column === sortBy && sortOrder === 'asc' ? 'desc' : 'asc';
            setSortBy(column);
            setSortOrder(order);

            const sortedData = [...data].sort((a, b) => {
                const aVal = a[column];
                const bVal = b[column];
                if (typeof aVal === 'string' || typeof bVal === 'string') {
                    const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), 'el');
                    return order === 'asc' ? cmp : -cmp;
                }
                const aNum = Number(aVal) || 0;
                const bNum = Number(bVal) || 0;
                return order === 'asc' ? aNum - bNum : bNum - aNum;
            });

            setData(sortedData);
        };

        const renderSortableHeader = ({ key, label }) => {
            const isActive = sortBy === key;
            const iconClass = !isActive
                ? 'bi-arrow-down-up sortable-col-icon sortable-col-icon--inactive'
                : sortOrder === 'asc'
                    ? 'bi-caret-up-fill sortable-col-icon'
                    : 'bi-caret-down-fill sortable-col-icon';

            return (
                <th
                    key={key}
                    className="sortable"
                    onClick={() => handleSort(key)}
                    aria-sort={
                        isActive
                            ? sortOrder === 'asc'
                                ? 'ascending'
                                : 'descending'
                            : 'none'
                    }
                >
                    <span className="sortable-col-head">
                        <span>{label}</span>
                        <i className={`bi ${iconClass}`} aria-hidden />
                    </span>
                </th>
            );
        };

    const isFormValid = educType !== '' && place !== '';

    const filterSummary = useMemo(() => {
        const educLabel = EDUC_OPTIONS.find((option) => option.value === educType)?.label || educType;
        return joinFilterSummaryParts([educLabel, place]);
    }, [educType, place]);

    return (
        <div id="root">
            <Header handleLogout={handleLogout}/>
            <main className='main-container ranking-per-state-page'>
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="non-reachable-places-sidebar-body"
                >
                        <Form>
                            <Form.Group className="mb-3 pt-3">
                                <Form.Label className='custom-label'>Τύπος εκπαίδευσης</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    name="educ_type"
                                    value={educType}
                                    onChange={(e) => {
                                        setEducType(e.target.value);
                                        setPlace("");
                                        setData(null);
                                    }}
                                    required
                                >
                                    <option value="">Επιλέξτε...</option>
                                    {EDUC_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className='custom-label'>Ενότητα εκπαίδευσης:</Form.Label>
                                <Form.Select className="custom-form" value={place} onChange={(e) => setPlace(e.target.value)} disabled={!educType || placeOptionsLoading}>
                                    <option>{placeOptionsLoading ? 'Φόρτωση...' : 'Επιλέξτε...'}</option>
                                    {!placeOptionsLoading && (placeOptions.length > 0 ? (
                                        placeOptions
                                            .slice()
                                            .sort((a, b) => a.localeCompare(b))
                                            .map((spec) => (
                                                <option key={spec} value={spec}>
                                                    {spec}
                                                </option>
                                            ))
                                    ) : (
                                        <option>No specialties available</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Form>
                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                variant="secondary"
                                type="button"
                                className="predict-submit-button"
                                onClick={handleSubmit}
                                disabled={!isFormValid}
                            >
                                Υποβολή
                            </Button>
                        </div>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                    {!data && !loading && (
                                        <p className="text-muted small mb-2">
                                            Μοριοδότηση σχολικών μονάδων ανά περιοχή και είδος εκπαίδευσης (εύρεση δυσπρόσιτων σχολείων)
                                        </p>
                                    )}
                                    {loading && (
                                        <div className="text-center mt-3 mb-3">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div>Φόρτωση αποτελεσμάτων...</div>
                                        </div>
                                    )}

                                    {data ? (
                                        <div className={!sidebarOpen ? 'results-snug-mobile' : ''}>
                                            <div className="d-flex align-items-center my-3">
                                                <h5 className="text-center flex-grow-1 m-0">Αποτελέσματα</h5>
                                            </div>
                                            <div className='table-outer-wrapper pb-3'>
                                                <div className="table-scroll-wrapper">
                                                    <Table bordered className="historical-results-table">
                                                        <thead>
                                                            <tr>
                                                                {TABLE_COLUMNS.map((column) => renderSortableHeader(column))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {data.map((row, index) => (
                                                                <tr key={index} className="cell-wrap">
                                                                    <td>{row.location}</td>
                                                                    <td>{row.deUnit}</td>
                                                                    <td>{row.community}</td>
                                                                    <td>{row.name}</td>
                                                                    <td>{row.category}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        </div>
                                    ) : !loading && null}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>
            <Footer />
        </div>
    )

}

export default NonReachablePlaces;